import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { createAgencyTheme } from '@/lib/theme';
import {
  APP_THEME_STORAGE_KEY,
  type AppThemeMode,
  getInitialAppThemeMode,
  getSystemThemeMode,
} from '@/lib/theme/mode';

interface AppThemeContextValue {
  mode: AppThemeMode;
  setMode: (mode: AppThemeMode) => void;
}

const AppThemeContext = createContext<AppThemeContextValue | undefined>(undefined);

function syncDocumentTheme(mode: AppThemeMode) {
  document.documentElement.classList.toggle('dark', mode === 'dark');
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AppThemeMode>(() => {
    if (typeof window === 'undefined') return 'dark';
    try {
      return getInitialAppThemeMode(window.localStorage);
    } catch {
      return getSystemThemeMode();
    }
  });

  const setMode = useCallback((nextMode: AppThemeMode) => {
    setModeState(nextMode);
    try {
      window.localStorage.setItem(APP_THEME_STORAGE_KEY, nextMode);
    } catch {
      // The in-memory preference still works when storage is unavailable.
    }
  }, []);

  useLayoutEffect(() => {
    syncDocumentTheme(mode);
  }, [mode]);

  const theme = useMemo(() => createAgencyTheme(mode), [mode]);
  const contextValue = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return (
    <AppThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
}

export type { AppThemeMode } from '@/lib/theme/mode';
